import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  CalendarDays, 
  Plus,
  Clock,
  Bell,
  CreditCard,
  Trash2,
  Edit,
  Check,
  AlertCircle,
  Loader2,
  Mail,
  Copy,
  RefreshCw,
  FileText,
  CheckCircle2,
  XCircle,
  Eye
} from "lucide-react";
import { format, addDays, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status badge colors + labels (calm tones)
const STATUS_CONFIG = {
  upcoming:     { label: 'Upcoming',     bg: 'bg-[#1a4d3e]/[0.06]', text: 'text-[#1a4d3e]/70' },
  due_soon:     { label: 'Due soon',     bg: 'bg-[#b8860b]/[0.08]', text: 'text-[#8b6914]' },
  overdue:      { label: 'Overdue',      bg: 'bg-[#8b4513]/[0.08]', text: 'text-[#8b4513]' },
  paid:         { label: 'Paid',         bg: 'bg-[#1a6b3e]/[0.08]', text: 'text-[#1a6b3e]' },
  needs_review: { label: 'Needs review', bg: 'bg-[#5a5a3d]/[0.06]', text: 'text-[#5a5a3d]' },
  active:       { label: 'Active',       bg: 'bg-[#1a4d3e]/[0.06]', text: 'text-[#1a4d3e]/70' },
};

function BillStatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.upcoming;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${config.bg} ${config.text}`} data-testid={`bill-status-${status}`}>
      {config.label}
    </span>
  );
}

// ===== Bills Inbox Card Component =====
function BillsInboxCard({ household, onImportComplete }) {
  const [inbox, setInbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imports, setImports] = useState([]);
  const [showImports, setShowImports] = useState(false);
  const [reviewItem, setReviewItem] = useState(null);
  const [reviewData, setReviewData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchInbox();
  }, [household]);

  const fetchInbox = async () => {
    try {
      const [inboxRes, importsRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/bills/inbox`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/bills/inbox/imports?limit=10`, { credentials: 'include' })
      ]);

      if (inboxRes.ok) {
        const data = await inboxRes.json();
        setInbox(data);
      }
      if (importsRes.ok) {
        const data = await importsRes.json();
        setImports(data.imports || []);
      }
    } catch (error) {
      console.error('Failed to fetch inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = () => {
    if (inbox?.email_alias) {
      navigator.clipboard.writeText(inbox.email_alias);
      setCopied(true);
      toast.success("Email copied");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const confirmBill = async (inboundId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/households/${household.household_id}/bills/inbox/imports/${inboundId}/confirm`,
        { method: 'POST', credentials: 'include' }
      );
      if (res.ok) {
        toast.success("Bill added. Reminder scheduled.");
        fetchInbox();
        onImportComplete?.();
      }
    } catch (error) {
      toast.error("Could not confirm bill. Please try again.");
    }
  };

  const openReview = async (inboundId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/households/${household.household_id}/bills/inbox/imports/${inboundId}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setReviewItem(data);
        setReviewData(data.extracted_data || {});
      }
    } catch (error) {
      toast.error("Could not load bill details. Please try again.");
    }
  };

  const submitReview = async () => {
    if (!reviewItem) return;
    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/households/${household.household_id}/bills/inbox/imports/${reviewItem.inbound_id}/review`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(reviewData)
        }
      );
      if (res.ok) {
        toast.success("Bill added. Reminder scheduled.");
        setReviewItem(null);
        fetchInbox();
        onImportComplete?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Could not create bill. Please check your entries.");
      }
    } catch (error) {
      toast.error("Could not submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteBill = async () => {
    if (!reviewItem) return;
    if (!window.confirm('Delete this bill? This cannot be undone.')) return;
    
    try {
      await fetch(
        `${API_URL}/api/households/${household.household_id}/bills/inbox/imports/${reviewItem.inbound_id}`,
        { method: 'DELETE', credentials: 'include' }
      );
      setReviewItem(null);
      fetchInbox();
    } catch (error) {
      toast.error("Could not delete. Please try again.");
    }
  };

  const dismissImport = async (inboundId) => {
    if (!window.confirm('Dismiss this import? You can re-import later if needed.')) return;
    try {
      await fetch(
        `${API_URL}/api/households/${household.household_id}/bills/inbox/imports/${inboundId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      fetchInbox();
    } catch (error) {
      console.error('Dismiss import error:', error);
      toast.error("Could not dismiss import. Please try again.");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'received': { label: 'Received', variant: 'outline', icon: Mail },
      'processing': { label: 'Processing', variant: 'secondary', icon: RefreshCw },
      'extracted': { label: 'Ready', variant: 'default', icon: CheckCircle2 },
      'needs_review': { label: 'Needs Review', variant: 'warning', icon: Eye },
      'confirmed': { label: 'Added', variant: 'success', icon: Check },
      'failed': { label: 'Failed', variant: 'destructive', icon: XCircle },
      'duplicate': { label: 'Duplicate', variant: 'secondary', icon: Copy }
    };
    const config = statusConfig[status] || statusConfig['received'];
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="text-xs">
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Check for low confidence fields
  const getLowConfidenceFields = () => {
    if (!reviewItem?.extracted_data?.confidence_scores) return [];
    const scores = reviewItem.extracted_data.confidence_scores;
    return Object.entries(scores)
      .filter(([_, score]) => score < 0.8)
      .map(([field]) => field);
  };

  if (loading) return null;
  if (!inbox?.enabled) return null;

  const pendingImports = imports.filter(i => ['needs_review', 'extracted', 'processing'].includes(i.status));
  const lowConfidenceFields = getLowConfidenceFields();

  return (
    <>
      {/* Inbox Card - Calm ARA Wealth UX */}
      <div className="card-dashboard mb-6" data-testid="bills-inbox-card">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
              <Mail className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-medium">Bills Inbox</h3>
              <p className="text-xs text-muted-foreground">Forward your bills here</p>
            </div>
          </div>
          {pendingImports.length > 0 && (
            <Badge variant="secondary">{pendingImports.length} pending</Badge>
          )}
        </div>

        {/* Calm Microcopy */}
        <p className="text-sm text-muted-foreground mb-4">
          Forward your digital bills here and we'll take care of the details.
          We'll extract the amount and due date, add it to your Bills list, and schedule reminders automatically.
        </p>
        
        <p className="text-xs text-muted-foreground mb-3">
          Send invoices or PDF bills to:
        </p>

        {/* Email Address */}
        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 mb-3">
          <code className="text-sm flex-1 truncate font-mono">{inbox.email_alias}</code>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={copyEmail}
            className="flex-shrink-0"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            <span className="ml-1 text-xs">{copied ? 'Copied' : 'Copy'}</span>
          </Button>
        </div>

        {/* Reassurance */}
        <p className="text-xs text-muted-foreground italic">
          You'll always have a chance to review before anything is saved.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Only visible to your household.
        </p>

        {/* Pending Imports */}
        {pendingImports.length > 0 && (
          <div className="space-y-2 border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Inbox Imports</span>
              <Button variant="link" size="sm" onClick={() => setShowImports(true)} className="p-0 h-auto">
                Review imports
              </Button>
            </div>
            {pendingImports.slice(0, 3).map(imp => (
              <div key={imp.inbound_id} className="flex items-center justify-between p-3 rounded-lg border">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{imp.subject || 'No subject'}</p>
                    <p className="text-xs text-muted-foreground">
                      {imp.status === 'processing' ? 'Bill received. Extracting details…' : imp.from_email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(imp.status)}
                  {imp.status === 'extracted' && (
                    <Button size="sm" onClick={() => confirmBill(imp.inbound_id)}>
                      Add
                    </Button>
                  )}
                  {imp.status === 'needs_review' && (
                    <Button size="sm" variant="outline" onClick={() => openReview(imp.inbound_id)}>
                      Review
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Imports Dialog */}
      <Dialog open={showImports} onOpenChange={setShowImports}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inbox Imports</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {imports.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No bill imports yet. Bills will appear here when detected from your email inbox.
              </p>
            ) : (
              imports.map(imp => (
                <div key={imp.inbound_id} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{imp.subject || 'No subject'}</p>
                    <p className="text-xs text-muted-foreground">
                      {imp.from_email} • {new Date(imp.received_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {getStatusBadge(imp.status)}
                    {imp.status === 'extracted' && (
                      <Button size="sm" onClick={() => { confirmBill(imp.inbound_id); setShowImports(false); }}>
                        Add
                      </Button>
                    )}
                    {imp.status === 'needs_review' && (
                      <Button size="sm" variant="outline" onClick={() => { openReview(imp.inbound_id); setShowImports(false); }}>
                        Review
                      </Button>
                    )}
                    {['failed', 'duplicate'].includes(imp.status) && (
                      <Button size="sm" variant="ghost" onClick={() => dismissImport(imp.inbound_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog - Calm ARA Wealth UX */}
      <Dialog open={!!reviewItem} onOpenChange={() => setReviewItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Review Bill Details</DialogTitle>
          </DialogHeader>
          {reviewItem && (
            <div className="space-y-4">
              {/* Intro Text */}
              <p className="text-sm text-muted-foreground">
                We've extracted the details from this bill.
                Please take a moment to confirm everything looks right.
              </p>
              
              {/* Low Confidence Warning */}
              {lowConfidenceFields.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Some details may need your attention. We've highlighted anything we weren't certain about.
                  </p>
                </div>
              )}

              {/* Email Info */}
              <div className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="font-medium">{reviewItem.subject}</p>
                <p className="text-xs text-muted-foreground">From: {reviewItem.from_email}</p>
              </div>

              {/* Form Fields */}
              <div className="space-y-3">
                <div>
                  <Label className={lowConfidenceFields.includes('biller_name') ? 'text-amber-600' : ''}>
                    Biller {lowConfidenceFields.includes('biller_name') && <span className="text-xs">(needs attention)</span>}
                  </Label>
                  <Input
                    value={reviewData.biller_name || ''}
                    onChange={(e) => setReviewData({ ...reviewData, biller_name: e.target.value })}
                    placeholder="e.g., Contact Energy"
                    className={lowConfidenceFields.includes('biller_name') ? 'border-amber-400' : ''}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className={lowConfidenceFields.includes('amount_due') ? 'text-amber-600' : ''}>
                      Amount Due {lowConfidenceFields.includes('amount_due') && <span className="text-xs">*</span>}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={reviewData.amount_due || ''}
                      onChange={(e) => setReviewData({ ...reviewData, amount_due: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className={lowConfidenceFields.includes('amount_due') ? 'border-amber-400' : ''}
                    />
                  </div>
                  <div>
                    <Label className={lowConfidenceFields.includes('due_date') ? 'text-amber-600' : ''}>
                      Due Date {lowConfidenceFields.includes('due_date') && <span className="text-xs">*</span>}
                    </Label>
                    <Input
                      type="date"
                      value={reviewData.due_date || ''}
                      onChange={(e) => setReviewData({ ...reviewData, due_date: e.target.value })}
                      className={lowConfidenceFields.includes('due_date') ? 'border-amber-400' : ''}
                    />
                  </div>
                </div>
                <div>
                  <Label>Reference</Label>
                  <Input
                    value={reviewData.invoice_number || reviewData.payment_reference || ''}
                    onChange={(e) => setReviewData({ ...reviewData, invoice_number: e.target.value })}
                    placeholder="Invoice or reference number"
                  />
                </div>
                <div>
                  <Label>Payment Details</Label>
                  <Input
                    value={reviewData.bank_account || ''}
                    onChange={(e) => setReviewData({ ...reviewData, bank_account: e.target.value })}
                    placeholder="Bank account (optional)"
                  />
                </div>
              </div>

              {/* Reassurance */}
              <p className="text-xs text-muted-foreground italic">
                Nothing will be scheduled or marked until you confirm.
              </p>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <Button 
                  variant="link" 
                  className="text-destructive p-0 h-auto" 
                  onClick={deleteBill}
                >
                  Delete Bill
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setReviewItem(null)}>
                    Edit Details
                  </Button>
                  <Button onClick={submitReview} disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Confirm & Add Bill
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function BillsContent() {
  return <BillsInner embedded />;
}

export default function BillsPage() {
  return <BillsInner />;
}

function BillsInner({ embedded = false }) {
  const { household, accounts } = useAuth();
  const Wrap = embedded ? ({children}) => <>{children}</> : AppLayout;
  const [bills, setBills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar');
  
  const [newBill, setNewBill] = useState({
    name: "",
    amount: "",
    frequency: "monthly",
    due_date: format(new Date(), 'yyyy-MM-dd'),
    autopay: false,
    account_id: "",
    category_id: ""
  });

  useEffect(() => {
    if (!household) return;
    fetchData();
  }, [household]);

  const fetchData = async () => {
    try {
      const [billsRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/bills`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/categories`, { credentials: 'include' })
      ]);

      if (billsRes.ok) {
        const data = await billsRes.json();
        setBills(data);
      }
      if (catRes.ok) setCategories(await catRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBill = async () => {
    if (!newBill.name || !newBill.amount) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/households/${household.household_id}/bills`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newBill,
          amount: parseFloat(newBill.amount),
          due_date: new Date(newBill.due_date).toISOString(),
          reminders: [1, 3, 7]
        })
      });

      if (!response.ok) throw new Error('Failed to add bill');

      toast.success("Bill added");
      setShowAddDialog(false);
      setNewBill({
        name: "",
        amount: "",
        frequency: "monthly",
        due_date: format(new Date(), 'yyyy-MM-dd'),
        autopay: false,
        account_id: "",
        category_id: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Delete this bill? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/households/${household.household_id}/bills/${billId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not delete bill');
      }
      toast.success("Bill deleted");
      fetchData();
    } catch (error) {
      console.error('Bill delete error:', error);
      toast.error(error.message || "Could not delete bill. Please try again.");
    }
  };

  const formatCurrency = (amount) => {
    const currency = household?.currency || 'NZD';
    return new Intl.NumberFormat('en-NZ', { style: 'currency', currency }).format(amount);
  };

  // Calculate bill load for calendar
  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getBillsForDay = (date) => {
    return bills.filter(bill => {
      const dueDate = new Date(bill.next_due);
      return isSameDay(dueDate, date);
    });
  };

  const totalMonthlyBills = bills.reduce((sum, bill) => {
    if (bill.frequency === 'monthly') return sum + bill.amount;
    if (bill.frequency === 'weekly') return sum + (bill.amount * 4.33);
    if (bill.frequency === 'fortnightly') return sum + (bill.amount * 2.17);
    if (bill.frequency === 'quarterly') return sum + (bill.amount / 3);
    if (bill.frequency === 'yearly') return sum + (bill.amount / 12);
    return sum;
  }, 0);

  const upcomingBills = bills
    .filter(bill => new Date(bill.next_due) <= addDays(new Date(), 14))
    .sort((a, b) => new Date(a.next_due) - new Date(b.next_due));

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="p-6 lg:p-8" data-testid="bills-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold">Bills</h1>
            <p className="text-muted-foreground">Track and manage your recurring bills</p>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-bill-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Bill
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Bill</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Bill Name *</Label>
                  <Input
                    value={newBill.name}
                    onChange={(e) => setNewBill({ ...newBill, name: e.target.value })}
                    placeholder="e.g., Power - Contact Energy"
                    data-testid="bill-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newBill.amount}
                      onChange={(e) => setNewBill({ ...newBill, amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="bill-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <select
                      value={newBill.frequency}
                      onChange={(e) => setNewBill({ ...newBill, frequency: e.target.value })}
                      className="w-full h-10 rounded-md border border-input bg-background px-3"
                      data-testid="bill-frequency-select"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={newBill.due_date}
                    onChange={(e) => setNewBill({ ...newBill, due_date: e.target.value })}
                    data-testid="bill-date-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Account</Label>
                    <select
                      value={newBill.account_id}
                      onChange={(e) => setNewBill({ ...newBill, account_id: e.target.value })}
                      className="w-full h-10 rounded-md border border-input bg-background px-3"
                    >
                      <option value="">Select account</option>
                      {accounts.map(acc => (
                        <option key={acc.account_id} value={acc.account_id}>{acc.display_name || acc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <select
                      value={newBill.category_id}
                      onChange={(e) => setNewBill({ ...newBill, category_id: e.target.value })}
                      className="w-full h-10 rounded-md border border-input bg-background px-3"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
                  <div>
                    <Label>Autopay Enabled</Label>
                    <p className="text-xs text-muted-foreground">Bill is automatically paid</p>
                  </div>
                  <Switch
                    checked={newBill.autopay}
                    onCheckedChange={(checked) => setNewBill({ ...newBill, autopay: checked })}
                    data-testid="bill-autopay-switch"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button onClick={handleAddBill} className="btn-primary" data-testid="save-bill-btn">
                    Add Bill
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Bills Inbox Card */}
        <BillsInboxCard household={household} onImportComplete={fetchData} />

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card-dashboard">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="font-medium">Monthly Total</span>
            </div>
            <p className="font-heading text-3xl font-bold" data-testid="monthly-total">
              {formatCurrency(totalMonthlyBills)}
            </p>
            <p className="text-sm text-muted-foreground">{bills.length} bills tracked</p>
          </div>
          
          <div className="card-dashboard">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <span className="font-medium">Due Soon</span>
            </div>
            <p className="font-heading text-3xl font-bold">{upcomingBills.length}</p>
            <p className="text-sm text-muted-foreground">in the next 14 days</p>
          </div>
          
          <div className="card-dashboard">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                <Check className="w-5 h-5 text-blue-600" />
              </div>
              <span className="font-medium">On Autopay</span>
            </div>
            <p className="font-heading text-3xl font-bold">
              {bills.filter(b => b.autopay).length}
            </p>
            <p className="text-sm text-muted-foreground">bills automated</p>
          </div>
        </div>

        {/* Sync note — calm, informational */}
        {bills.length > 0 && (
          <p className="text-[11px] text-muted-foreground/50 mb-4" data-testid="bills-sync-note">
            Bills are automatically reflected in your planned expenses and Flow breakdown.
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar View */}
          <div className="lg:col-span-2 card-dashboard">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Bill Calendar</h3>
              <div className="flex items-center gap-2">
                <Button 
                  variant={viewMode === 'calendar' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                >
                  Calendar
                </Button>
                <Button 
                  variant={viewMode === 'list' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  List
                </Button>
              </div>
            </div>

            {viewMode === 'calendar' ? (
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md border"
                  modifiers={{
                    hasBill: daysInMonth.filter(day => getBillsForDay(day).length > 0)
                  }}
                  modifiersStyles={{
                    hasBill: { 
                      backgroundColor: 'hsl(var(--amber-100))',
                      borderRadius: '50%'
                    }
                  }}
                />
              </div>
            ) : (
              <div className="space-y-2">
                {bills.length === 0 && (
                  <div className="text-center py-8">
                    <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No bills yet. Add one to get started.</p>
                  </div>
                )}
                {bills.map(bill => {
                  const dueDate = new Date(bill.next_due);
                  const status = bill.display_status || 'upcoming';
                  
                  return (
                    <div 
                      key={bill.bill_id}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                      data-testid={`bill-${bill.bill_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-stone-100 dark:bg-stone-800/30">
                          <CalendarDays className="w-5 h-5 text-stone-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{bill.name}</p>
                            <BillStatusBadge status={status} />
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{format(dueDate, 'MMM d')}</span>
                            <span>.</span>
                            <span className="capitalize">{bill.frequency}</span>
                            {bill.autopay && (
                              <>
                                <span>.</span>
                                <Badge variant="outline" className="text-xs">Autopay</Badge>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <span className="font-medium tabular-nums">{formatCurrency(bill.amount)}</span>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteBill(bill.bill_id)}
                          data-testid={`delete-bill-${bill.bill_id}`}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Upcoming Bills Sidebar */}
          <div className="card-dashboard">
            <h3 className="font-heading font-semibold mb-4">Coming Up</h3>
            
            {upcomingBills.length === 0 ? (
              <div className="text-center py-8">
                <Check className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Nothing due soon</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingBills.map(bill => {
                  const dueDate = new Date(bill.next_due);
                  const today = new Date();
                  const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <div 
                      key={bill.bill_id}
                      className="p-4 rounded-lg border border-border"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium">{bill.name}</p>
                        <BillStatusBadge status={bill.display_status || 'upcoming'} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {daysUntil <= 0 ? 'Due today' : `Due in ${daysUntil} days`}
                        </span>
                        <span className="font-medium tabular-nums">{formatCurrency(bill.amount)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Wrap>
  );
}
